import Immutable from 'immutable';
import Moment from 'moment';

import FARE_SEARCH_STATUS from '../../scripts/constants/fare-search-status-enum';
import FARE_TYPES from '../../scripts/constants/fare-types';

/**
 * Notice that current implementation makes an assumption since the MAX_MONTHS is below 12.
 * There will be no overlapping month number so calendar uses the month number as a key in the months map.
 */
const MAX_MONTHS = 12;

const CalendarDayUIModel = Immutable.Record({
  bestFare: null,
  bestReturn: null,
  dayNumber: null,
  isDiscounted: false,
  isInactive: true,
  isPromoAvailable: false,
  isSoldOut: false,
});

export { CalendarDayUIModel };

const CalendarMonthUIModel = Immutable.Record({
  days: Immutable.List(),
  monthBestFare: null,
  monthMoment: null,
  monthName: null,
  monthNumber: null,
  monthShortName: null,
  year: null,
});

export { CalendarMonthUIModel };

/**
 * Represents the calendar UI grid, through a nested immutable structure:
 *
 * - A Map of months (month number => CalendarMonthUIModel)
 * - Each CalendarMonthUIModel has a List of CalendarDayUIModel
 * - Each CalendarDayUIModel represents a day in the grid, including empty days at the beginning and end of the month
 * to keep the grip NxN.
 */
class CalendarUIModel extends Immutable.Record({
  isDiscountFareAvailable: false,
  isPromoFareAvailable: false,
  months: new Immutable.OrderedMap(),
}) {
  getAllMonths() {
    return this.months.toArray();
  }

  getMonthByIndex(index) {
    let selectedMonth = null;

    this.months.valueSeq().forEach((month, loopIndex) => {
      if (loopIndex === index) {
        selectedMonth = month;
        return false;
      }
    });

    return selectedMonth;
  }

  /**
   * Creates a calendar.
   *
   * @param  {Date} today - defines the first date of the calendar
   *
   * @return {CalendarUIModel}
   */
  static create(today, renderEntireFirstMonth = false) {
    let calendar = new CalendarUIModel();
    const initialMoment = Moment(today);

    // Iterate through months
    for (let monthIndex = 0; monthIndex < MAX_MONTHS; monthIndex++) {
      const newMonth = initialMoment.clone().add(monthIndex, 'months');

      const daysInMonth = newMonth.daysInMonth();

      // Day to start rendering month (typically 1st,
      // but current month starts today)
      let lastMonthDayOfWeek = null;
      let startDay = null;
      let startingDayOfTheWeek = null;

      if (monthIndex == 0 && !renderEntireFirstMonth) {
        // If rendering current month
        startDay = initialMoment.format('D');
        startingDayOfTheWeek = initialMoment.weekday();
      } else {
        // Days of week as per ISO / Moment
        // Monday = 1
        // http://momentjs.com/docs/#/get-set/iso-weekday/
        startDay = 1;
        newMonth.startOf('month');
        startingDayOfTheWeek = newMonth.weekday();
      }

      startDay = parseInt(startDay, 10);
      // Days of week as per ISO / Moment
      // Saturday = 6
      // http://momentjs.com/docs/#/get-set/iso-weekday/
      lastMonthDayOfWeek = 6 - newMonth.endOf('month').weekday();

      let dayList = Immutable.List();

      // Back-filling first week of month with blank days
      if (startingDayOfTheWeek) {
        for (let dayOffset = 0; dayOffset < startingDayOfTheWeek; dayOffset++) {
          const day = startDay - startingDayOfTheWeek + dayOffset;

          dayList = dayList.push(
            new CalendarDayUIModel({
              dayNumber: day > 0 ? day : null,
              isInactive: true,
            })
          );
        }
      }

      // Filling rest of the month
      for (let dayNumber = startDay; dayNumber <= daysInMonth; dayNumber++) {
        const isInactive = renderEntireFirstMonth && monthIndex === 0 && dayNumber < initialMoment.format('D');

        dayList = dayList.push(new CalendarDayUIModel({
          dayNumber: dayNumber,
          isInactive: isInactive,
        }));
      }

      // Filling last week of the month with blank days
      if (lastMonthDayOfWeek) {
        for (let dayOffset = 0; dayOffset < lastMonthDayOfWeek; dayOffset++) {
          dayList = dayList.push(
            new CalendarDayUIModel({
              isInactive: true,
            })
          );
        }
      }

      // Push to months array
      const monthRecord = new CalendarMonthUIModel({
        days: dayList,
        monthMoment: newMonth,
        monthName: newMonth.format('MMMM'),
        monthNumber: newMonth.month(),
        monthShortName: newMonth.format('MMM'),
        year: newMonth.year(),
      });

      calendar = calendar.setIn(['months', newMonth.month()], monthRecord);
    }

    return calendar;
  }

  /**
   * Makes all days unavailable in the calendar, up to the boundary date.
   *
   * @param  {Date} dateBoundary - a date up to which the calendar will be unavailable (not including the date)
   *
   * @return {CalendarUIModel} - an updated instance of the calendar
   */
  makeAllDatesUnavailableUpTo(dateBoundary) {
    const boundaryMoment = Moment(dateBoundary);
    let updatedCalendar = this;

    for (const month of this.getAllMonths()) {
      month.days.forEach((day, index) => {
        if (day.dayNumber) {
          const dayMoment = Moment({
            day: day.dayNumber,
            month: month.monthNumber,
            year: month.year,
          });

          if (dayMoment.isSame(boundaryMoment, 'day') || dayMoment.isAfter(boundaryMoment, 'day')) {
            return false;
          }

          updatedCalendar = updatedCalendar.setIn(
            ['months', month.monthNumber, 'days', index], day.set('isInactive', true)
          );
        }
      });
    }

    return updatedCalendar;
  }

  /**
   * Updates calendar days availability in a month/year with the contents of a BestFaresUIModel.
   *
   * @param  {integer} month
   * @param  {integer} year
   * @param  {BestFaresUIModel} bestFaresUIModel - the result of an API call
   *
   * @return {CalendarUIModel} - an updated instance of the calendar
   */
  updateWithBestFares(month, year, bestFaresUIModel) {
    let updatedCalendar = this;
    let bestMonthFare = null;

    this.months.get(month).days.forEach((day, index) => {
      if (day.dayNumber) {
        const status = bestFaresUIModel.getAvailability(day.dayNumber, month, year);
        const isFareAvailable = status === FARE_SEARCH_STATUS.AVAILABLE;
        const isDiscounted = isFareAvailable ? bestFaresUIModel.getDiscounted(day.dayNumber, month, year) : false;
        const bestFare = isFareAvailable ? bestFaresUIModel.getBestFare(day.dayNumber, month, year) : null;
        const isPromoAvailable = isFareAvailable && bestFaresUIModel.getFareType(day.dayNumber, month, year) === FARE_TYPES.FARE_TYPES.COACH_EC;
        const bestReturn = bestFaresUIModel.getBestReturn(day.dayNumber, month, year);

        const isInactive = status === undefined || !isFareAvailable;
        const isSoldOut = status === FARE_SEARCH_STATUS.SOLDOUT;

        updatedCalendar = updatedCalendar.setIn(
          ['months', month, 'days', index],
          day.set('isInactive', isInactive)
            .set('isSoldOut', isSoldOut)
            .set('isDiscounted', isDiscounted)
            .set('bestFare', bestFare)
            .set('isPromoAvailable', isPromoAvailable)
            .set('bestReturn', bestReturn)
        );

        if (isPromoAvailable && !updatedCalendar.isPromoFareAvailable) {
          updatedCalendar = updatedCalendar.set('isPromoFareAvailable', true);
        }

        if (isDiscounted && !updatedCalendar.isDiscountFareAvailable) {
          updatedCalendar = updatedCalendar.set('isDiscountFareAvailable', true);
        }

        if (bestFare && (bestMonthFare === null || bestFare.total && bestFare.total > 0 && bestFare.total < bestMonthFare.total)) {
          bestMonthFare = bestFare;
        }
      }
    });

    updatedCalendar = updatedCalendar.setIn(['months', month, 'monthBestFare'], bestMonthFare);

    return updatedCalendar;
  }

  getInactiveByDay(monthObj, dayObj) {
    let isInactive = false;

    if (dayObj) {
      const month = this.months.find(month => {
        return month.monthNumber === monthObj.monthNumber
          && month.year === monthObj.year;
      });

      if (month) {
        const day = month.days.find(day => {
          return day.dayNumber === dayObj.dayNumber;
        });

        if (day) {
          isInactive = day.isInactive;
        }
      }
    }

    return isInactive;
  }
}

export default CalendarUIModel;
